import type { Margin, NumericRange, Size } from '../types';
import { toAxisTicks, type AxisPadding } from '../data/horizontalAxes';

export type Axes2dRenderArgs = {
  context: CanvasRenderingContext2D;
  viewport: Size;
  margin: Margin;
  xTitle: string;
  yTitle: string;
  xRange: NumericRange;
  yRange: NumericRange;
  formatX: (value: number) => string;
  formatY: (value: number) => string;
  gridColor: string;
  inkColor: string;
  /** Divisions of each axis, chosen for that axis' own range. One per axis, as in 3D. */
  xTickCount: number;
  yTickCount: number;
  /** How far each range runs past its first and last tick, in field units. */
  xPadding: AxisPadding;
  yPadding: AxisPadding;
};

const TICK_LABEL_FONT = '11px system-ui, -apple-system, "Segoe UI", sans-serif';
const TITLE_FONT = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';

/** Gap between a y tick label and the plot edge, and between the label and the rotated title. */
const LABEL_GAP = 8;

/** Horizontal band the rotated y title occupies, from the canvas edge. */
const TITLE_BAND = 26;

export type MeasuredMarginArgs = {
  context: CanvasRenderingContext2D;
  margin: Margin;
  xRange: NumericRange;
  yRange: NumericRange;
  formatX: (value: number) => string;
  formatY: (value: number) => string;
  xTickCount: number;
  yTickCount: number;
  xPadding: AxisPadding;
  yPadding: AxisPadding;
};

/**
 * Widens a margin until the axis labels fit inside it.
 *
 * The gutters cannot be a constant, because `format` belongs to the host and the ranges are the
 * field's own units - so the same chart carries `0.4` on one viewpoint and `1,250,000` on the
 * next. At the fixed defaults a five-character label overlapped the rotated y title and a
 * nine-character one ran off the canvas, which is an ordinary cost or headcount field rather than
 * a contrived one.
 *
 * Measured rather than estimated from a character count: the labels are drawn in this font by this
 * context, so `measureText` is the same answer the renderer will produce, and a host that formats
 * with a currency symbol or a unit suffix is covered without the estimate having anticipated it.
 *
 * Only ever widens. A caller asking for a larger gutter than the labels need has asked for
 * whitespace, which is theirs to spend.
 */
export const toMeasuredMargin = ({
  context,
  margin,
  xRange,
  yRange,
  formatX,
  formatY,
  xTickCount,
  yTickCount,
  xPadding,
  yPadding,
}: MeasuredMarginArgs): Margin => {
  context.save();
  context.font = TICK_LABEL_FONT;

  const widest = (
    range: NumericRange,
    tickCount: number,
    padding: AxisPadding,
    format: (value: number) => string
  ): number =>
    toAxisTicks({ range, tickCount, padding }).reduce(
      (widest, { value }) =>
        Math.max(widest, context.measureText(format(value)).width),
      0
    );

  const yLabels = widest(yRange, yTickCount, yPadding, formatY);
  const xLabels = widest(xRange, xTickCount, xPadding, formatX);
  context.restore();

  return {
    ...margin,
    left: Math.max(margin.left, TITLE_BAND + LABEL_GAP + yLabels),
    /* The last x tick is centred on the plot's right edge, so half its label hangs past it. */
    right: Math.max(margin.right, Math.ceil(xLabels / 2) + LABEL_GAP),
  };
};

/**
 * Frame and ticks for the flat charts.
 *
 * Drawn after the field rather than before it: a filled contour covers the whole plot area,
 * so gridlines underneath would be invisible and ticks would be swallowed. Only the frame
 * and the outside ticks are drawn, with no gridlines across the field - lines over a
 * continuous surface read as contours, and there are already real ones there.
 */
export const renderAxes2d = ({
  context,
  viewport,
  margin,
  xTitle,
  yTitle,
  xRange,
  yRange,
  formatX,
  formatY,
  gridColor,
  inkColor,
  xTickCount,
  yTickCount,
  xPadding,
  yPadding,
}: Axes2dRenderArgs): void => {
  const left = margin.left;
  const top = margin.top;
  const width = viewport.width - margin.left - margin.right;
  const height = viewport.height - margin.top - margin.bottom;
  if (width <= 0 || height <= 0) return;

  context.save();

  context.strokeStyle = gridColor;
  context.lineWidth = 1;
  context.strokeRect(left, top, width, height);

  context.fillStyle = inkColor;
  context.font = TICK_LABEL_FONT;

  context.textAlign = 'center';
  context.textBaseline = 'top';
  for (const { value, fraction } of toAxisTicks({
    range: xRange,
    tickCount: xTickCount,
    padding: xPadding,
  })) {
    const x = left + fraction * width;
    context.beginPath();
    context.moveTo(x, top + height);
    context.lineTo(x, top + height + 4);
    context.stroke();
    context.fillText(formatX(value), x, top + height + 8);
  }

  context.textAlign = 'right';
  context.textBaseline = 'middle';
  for (const { value, fraction } of toAxisTicks({
    range: yRange,
    tickCount: yTickCount,
    padding: yPadding,
  })) {
    /* Screen y grows downward, so the low end of the range sits at the bottom. */
    const y = top + height - fraction * height;
    context.beginPath();
    context.moveTo(left - 4, y);
    context.lineTo(left, y);
    context.stroke();
    context.fillText(formatY(value), left - LABEL_GAP, y);
  }

  context.font = TITLE_FONT;
  context.textAlign = 'center';
  context.textBaseline = 'bottom';
  context.fillText(xTitle, left + width / 2, viewport.height - 6);

  context.save();
  context.translate(12, top + height / 2);
  context.rotate(-Math.PI / 2);
  context.textBaseline = 'top';
  context.fillText(yTitle, 0, 0);
  context.restore();

  context.restore();
};
